// API client — calls Pages Functions at /api/*
// During dev (`npm run dev`), Vite serves on :5173 and the API is unreachable
// unless you run `npx wrangler pages dev` (handles both static + functions).
// In production (Cloudflare Pages), same-origin /api/* hits the Functions runtime.

import type { Client, ClientBundle, LfFullScan, LfAIScanFull, AIPlatformLF, ShareLink, Role, PageId, IntakeRow, ClientStatusResponse, OverviewPayload, CompetitorRoster } from '../types';

export interface UserDto {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  clientId: string | null;
  allowedPages: PageId[] | null; // null = use role default
  status: 'active' | 'disabled';
  createdAt: number;
  lastLoginAt: number | null;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, `${res.status} ${res.statusText} — ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function listClients(): Promise<Client[]> {
  const { clients } = await apiFetch<{ clients: Client[] }>('/api/clients');
  return clients;
}

export async function getClientBundle(id: string): Promise<ClientBundle> {
  return apiFetch<ClientBundle>(`/api/clients/${encodeURIComponent(id)}`);
}

// Derived Dashboard payload — review benchmark, map-grid roll-up, AI summary.
export async function getClientOverview(id: string): Promise<OverviewPayload> {
  const data = await apiFetch<{ overview: OverviewPayload }>(
    `/api/clients/${encodeURIComponent(id)}/overview`,
  );
  return data.overview;
}

// ── Competitor roster override ──────────────────────────────────────────────
// The roster is chosen automatically; these let a PE veto a pick that is wrong.

export async function getCompetitorRoster(clientId: string): Promise<CompetitorRoster> {
  return apiFetch<CompetitorRoster>(`/api/clients/${encodeURIComponent(clientId)}/competitors`);
}

export async function excludeCompetitor(
  clientId: string, placeId: string, name?: string, reason?: string,
): Promise<void> {
  await apiFetch(`/api/clients/${encodeURIComponent(clientId)}/competitors`, {
    method: 'POST',
    body: JSON.stringify({ placeId, name, reason }),
  });
}

export async function restoreCompetitor(clientId: string, placeId: string): Promise<void> {
  await apiFetch(
    `/api/clients/${encodeURIComponent(clientId)}/competitors?placeId=${encodeURIComponent(placeId)}`,
    { method: 'DELETE' },
  );
}

export async function createClient(input: {
  name: string;
  location: string;
  category?: string;
  gbpPlaceId?: string;
  lat?: number;
  lng?: number;
  groupLabel?: string;
  parentClientId?: string;
  locationLabel?: string;
}): Promise<Client> {
  const { client } = await apiFetch<{ client: Client }>('/api/clients', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return client;
}

export async function updateClient(id: string, patch: Partial<{
  name: string;
  location: string;
  category: string;
  gbpPlaceId: string;
  lat: number;
  lng: number;
  aiKeywordsMode: 'auto' | 'manual';
  groupLabel: string | null;
  parentClientId: string | null;
  locationLabel: string | null;
}>): Promise<unknown> {
  return apiFetch(`/api/clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ── Per-user client access (whitelist) ───────────────────────

export async function getUserClientAccess(userId: string): Promise<string[]> {
  const { clientIds } = await apiFetch<{ clientIds: string[] }>(
    `/api/users/${encodeURIComponent(userId)}/clients`,
  );
  return clientIds ?? [];
}

export async function setUserClientAccess(userId: string, clientIds: string[]): Promise<void> {
  await apiFetch(`/api/users/${encodeURIComponent(userId)}/clients`, {
    method: 'PUT',
    body: JSON.stringify({ clientIds }),
  });
}

export async function deleteClient(id: string): Promise<void> {
  const res = await fetch(`/api/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    throw new ApiError(res.status, await res.text());
  }
}

// Delete a single Maps scan row (Manage Keywords UI). Does NOT touch the LF
// campaign — admin must remove keywords from Local Falcon directly to stop
// them from re-syncing.
export async function deleteLfScan(clientId: string, keyword: string): Promise<void> {
  const res = await fetch(
    `/api/clients/${encodeURIComponent(clientId)}/lf/scans/${encodeURIComponent(keyword)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

// One-button pipeline: Places refresh → LF Maps sync → AI Visibility → AI Insights.
export interface RunAllStep {
  step: 'place' | 'lf_maps' | 'ai_visibility' | 'insights';
  status: 'ok' | 'skipped' | 'failed';
  message?: string;
  details?: unknown;
}
export async function runAllForClient(clientId: string): Promise<{ ok: true; steps: RunAllStep[] }> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/run-all`, { method: 'POST' });
}

// ── Share links ───────────────────────────────────────────────

export async function createShareLink(clientId: string): Promise<{ token: string; path: string }> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/share`, { method: 'POST' });
}

export async function listShareLinks(clientId: string): Promise<ShareLink[]> {
  const { links } = await apiFetch<{ links: ShareLink[] }>(`/api/clients/${encodeURIComponent(clientId)}/share`);
  return links;
}

export async function revokeShareLink(token: string): Promise<void> {
  await fetch(`/api/share/${encodeURIComponent(token)}`, { method: 'DELETE' });
}

// The share endpoint embeds the Dashboard overview alongside the bundle —
// anonymous viewers can't call the authed overview route themselves.
export async function getPublicShareBundle(token: string): Promise<ClientBundle & { overview?: OverviewPayload | null }> {
  return apiFetch<ClientBundle & { overview?: OverviewPayload | null }>(`/api/public/share/${encodeURIComponent(token)}`);
}

// ── Users (admin only) ────────────────────────────────────────

export async function listUsers(): Promise<UserDto[]> {
  const { users } = await apiFetch<{ users: UserDto[] }>('/api/users');
  return users;
}

export async function inviteUser(input: {
  email: string;
  name?: string;
  role: Role;
  clientId?: string | null;
  allowedPages?: PageId[] | null;
}): Promise<UserDto> {
  const { user } = await apiFetch<{ user: UserDto }>('/api/users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return user;
}

export async function updateUser(id: string, patch: Partial<{
  name: string | null;
  role: Role;
  clientId: string | null;
  allowedPages: PageId[] | null;
  status: 'active' | 'disabled';
}>): Promise<void> {
  await apiFetch(`/api/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteUser(id: string): Promise<void> {
  await fetch(`/api/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface MagicLinkResponse {
  token: string;
  link: string;
  email: string;
  expiresAt: number;
}

// Admin generates a 15-min magic link for a user; admin then shares the URL externally.
export async function generateUserMagicLink(userId: string): Promise<MagicLinkResponse> {
  return apiFetch<MagicLinkResponse>(`/api/users/${encodeURIComponent(userId)}/magic-link`, {
    method: 'POST',
  });
}

// ── Places (Track A) ─────────────────────────────────────────

export async function refreshClientPlaceData(clientId: string): Promise<unknown> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/place/refresh`, { method: 'POST' });
}

// ── Local Falcon (Track C) ───────────────────────────────────

// Refresh one keyword (or the latest scan if no keyword given).
export async function refreshClientLfScan(clientId: string, opts?: { keyword?: string; reportKey?: string }): Promise<unknown> {
  const qs = new URLSearchParams();
  if (opts?.keyword) qs.set('keyword', opts.keyword);
  if (opts?.reportKey) qs.set('reportKey', opts.reportKey);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/lf/refresh${suffix}`, { method: 'POST' });
}

// Pull every keyword's latest scan for this client in one call.
export async function refreshAllLfScans(clientId: string): Promise<{ ok: true; synced: { keyword: string; reportKey: string; fetchedAt: number }[] }> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/lf/refresh?all=1`, { method: 'POST' });
}

// Fetch the full scan (with per-grid data_points + places) for one keyword.
export async function getLfScan(clientId: string, keyword: string): Promise<LfFullScan> {
  const data = await apiFetch<{ scan: LfFullScan }>(
    `/api/clients/${encodeURIComponent(clientId)}/lf/scans/${encodeURIComponent(keyword)}`
  );
  return data.scan;
}

export interface LfScanListItem {
  reportKey: string;
  keyword: string;
  date: string;
  timestamp: number;
  gridSize: number;
  arp: number;
  atrp: number;
  solv: number;
  foundIn: number;
  heatmapUrl: string;
}

export async function listClientLfScans(clientId: string): Promise<LfScanListItem[]> {
  const data = await apiFetch<{ scans: LfScanListItem[] }>(`/api/clients/${encodeURIComponent(clientId)}/lf/refresh`);
  return data.scans ?? [];
}

// ── Local Falcon AI Scans ────────────────────────────────────

export interface AIRefreshResult {
  ok: true;
  triggered: { keyword: string; platform: AIPlatformLF; reportKey: string }[];
  cached:    { keyword: string; platform: AIPlatformLF; ageDays: number }[];
  failed:    { keyword: string; platform: AIPlatformLF; error: string }[];
}

export interface AISyncResult {
  ok: true;
  synced:  { keyword: string; platform: AIPlatformLF; reportKey: string }[];
  skipped: { keyword: string; platform: AIPlatformLF; reason: string }[];
  failed:  { keyword: string; platform: AIPlatformLF; error: string }[];
}

// Trigger new AI scans for stale (keyword × platform) pairs. 7-day dedup.
export async function refreshLfAIScans(clientId: string): Promise<AIRefreshResult> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/lf/ai-scan/refresh`, { method: 'POST' });
}

// Pull latest AI scan results from LF into D1.
export async function syncLfAIScans(clientId: string): Promise<AISyncResult> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/lf/ai-scan/sync`, { method: 'POST' });
}

// Fetch the full AI scan (with citation sources + ai_analysis) for one (keyword, platform).
export async function getLfAIScan(
  clientId: string,
  keyword: string,
  platform: AIPlatformLF,
): Promise<LfAIScanFull> {
  const data = await apiFetch<{ scan: LfAIScanFull }>(
    `/api/clients/${encodeURIComponent(clientId)}/lf/ai-scan/${encodeURIComponent(keyword)}/${encodeURIComponent(platform)}`,
  );
  return data.scan;
}

// ── Historical scan time series ─────────────────────────────────────────────
// Returned by /history endpoints. Sorted oldest → newest. Used by Heatmap's
// History tab, AI Visibility's Trend tab, and the delta badge baseline lookup.

export interface LfScanHistoryEntry {
  reportKey: string;
  scanDate: number;
  gridSize: number;
  arp: number | null;
  atrp: number | null;
  solv: number | null;
  foundIn: number | null;
  uniqueCompetitors: number | null;
  pdfUrl: string | null;
  fetchedAt: number;
}

export async function getLfScanHistory(clientId: string, keyword: string): Promise<LfScanHistoryEntry[]> {
  const data = await apiFetch<{ scans: LfScanHistoryEntry[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/lf/scans/${encodeURIComponent(keyword)}/history`,
  );
  return data.scans ?? [];
}

export interface LfAIScanHistoryEntry {
  reportKey: string;
  scanDate: number;
  saiv: number | null;
  arp: number | null;
  atrp: number | null;
  bps: number | null;
  foundIn: number | null;
  mentioned: boolean;
  fetchedAt: number;
}

export async function getLfAIScanHistory(
  clientId: string,
  keyword: string,
  platform: AIPlatformLF,
): Promise<LfAIScanHistoryEntry[]> {
  const data = await apiFetch<{ scans: LfAIScanHistoryEntry[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/lf/ai-scan/${encodeURIComponent(keyword)}/${encodeURIComponent(platform)}/history`,
  );
  return data.scans ?? [];
}

// ── AI Insights (LLM-generated recommendations) ──────────────

export async function generateAIInsights(clientId: string): Promise<unknown> {
  return apiFetch(`/api/clients/${encodeURIComponent(clientId)}/ai-insights/generate`, {
    method: 'POST',
  });
}

// ── AI Keywords (Settings management) ────────────────────────

export interface AIKeywordItem {
  keyword: string;
  createdAt: number;
}

export async function listAIKeywords(clientId: string): Promise<AIKeywordItem[]> {
  const data = await apiFetch<{ keywords: AIKeywordItem[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/ai-keywords`,
  );
  return data.keywords ?? [];
}

export async function addAIKeyword(clientId: string, keyword: string): Promise<void> {
  await apiFetch(`/api/clients/${encodeURIComponent(clientId)}/ai-keywords`, {
    method: 'POST',
    body: JSON.stringify({ keyword }),
  });
}

export async function deleteAIKeyword(clientId: string, keyword: string): Promise<void> {
  await fetch(
    `/api/clients/${encodeURIComponent(clientId)}/ai-keywords/${encodeURIComponent(keyword)}`,
    { method: 'DELETE' },
  );
}

// ── Google integrations (Track B) ────────────────────────────

export interface GoogleConnectionDto {
  id: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  googleEmail: string;
  expiresAt: number | null;
  scopes: string[];
  status: 'active' | 'revoked' | 'error';
  connectedAt: number;
  lastUsedAt: number | null;
  lastError: string | null;
  locationCount: number;
}

export async function listGoogleConnections(): Promise<GoogleConnectionDto[]> {
  const { connections } = await apiFetch<{ connections: GoogleConnectionDto[] }>('/api/integrations/google');
  return connections;
}

export async function disconnectGoogleConnection(id: string): Promise<void> {
  await fetch(`/api/integrations/google/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface GbpLocationOverview {
  id: string;
  placeId: string | null;
  displayName: string | null;
  primaryCategory: string | null;
  address: string | null;
  grantedVia: string;
  discoveredAt: number;
  lastSyncedAt: number | null;
  client: { id: string; name: string } | null;
  intake: { id: string; status: string; gbpName: string | null } | null;
  hasAccount: boolean;
  hasIntake: boolean;
}

export async function listGbpLocationsOverview(): Promise<GbpLocationOverview[]> {
  const { locations } = await apiFetch<{ locations: GbpLocationOverview[] }>('/api/integrations/google/locations');
  return locations;
}

export interface GbpSyncResult { locationsFound: number; locationsUpserted: number; error?: string }

export async function syncGoogleConnectionLocations(id: string): Promise<GbpSyncResult> {
  return apiFetch<GbpSyncResult>(`/api/integrations/google/${id}/sync`, { method: 'POST' });
}

// ── Client Intake workspace ──────────────────────────────────

export async function listIntake(): Promise<IntakeRow[]> {
  const { rows } = await apiFetch<{ rows: IntakeRow[] }>('/api/intake');
  return rows;
}

// Selectable Project Executives for the intake form = active manager accounts.
// Manager-accessible (unlike the admin-only GET /api/users).
export interface PeOption { id: string; name: string | null; email: string }
export async function listPeOptions(): Promise<PeOption[]> {
  const { options } = await apiFetch<{ options: PeOption[] }>('/api/intake/pe-options');
  return options;
}

export async function createIntake(): Promise<IntakeRow> {
  const { row } = await apiFetch<{ row: IntakeRow }>('/api/intake', { method: 'POST' });
  return row;
}

export async function getIntake(id: string): Promise<IntakeRow> {
  const { row } = await apiFetch<{ row: IntakeRow }>(`/api/intake/${encodeURIComponent(id)}`);
  return row;
}

// Partial update — any subset of editable IntakeRow fields, plus an optional
// explicit { status: 'archived' | 'draft' } toggle. `status` is otherwise
// server-recomputed, so the patch type excludes the full IntakeStatus union.
export type IntakePatch = Partial<Omit<IntakeRow, 'status'>> & { status?: 'archived' | 'draft' };

export async function updateIntake(
  id: string,
  patch: IntakePatch,
): Promise<IntakeRow> {
  const { row } = await apiFetch<{ row: IntakeRow }>(`/api/intake/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return row;
}

export type OnboardingJobStatus =
  | 'queued' | 'running' | 'waiting_external' | 'waiting_report'
  | 'waiting_quota' | 'needs_input' | 'completed' | 'failed';

export interface OnboardingJobStep {
  key: string;
  status: 'queued' | 'running' | 'waiting' | 'completed' | 'skipped' | 'failed';
  attempts: number;
  startedAt: number | null;
  endedAt: number | null;
  details: Record<string, unknown>;
  error: { code: string; message: string | null } | null;
  nextRetryAt: number | null;
}

export interface OnboardingJob {
  id: string;
  intakeId: string;
  clientId: string | null;
  mode: 'onboarding' | 'incremental' | 'repair' | 'scheduled';
  status: OnboardingJobStatus;
  currentStep: string | null;
  updatedAt: number;
  completedAt: number | null;
  nextRetryAt: number | null;
  error: { code: string; message: string | null } | null;
  steps: OnboardingJobStep[];
}

export async function startIntakeSetup(
  id: string,
  idempotencyKey: string,
): Promise<{ jobId: string; status: OnboardingJobStatus; created: boolean }> {
  return apiFetch(`/api/intake/${encodeURIComponent(id)}/start-setup`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export async function getOnboardingJob(jobId: string): Promise<OnboardingJob> {
  const { job } = await apiFetch<{ job: OnboardingJob }>(
    `/api/onboarding/jobs/${encodeURIComponent(jobId)}`,
  );
  return job;
}

// ── Cloud maintenance / bulk smart-update (admin) ────────────────────────
export interface CompletenessAuditDto {
  generatedAt: number;
  summary: Record<string, number>;
  estimate: {
    localFalconCredits: number;
    dataForSeoAiPairs: number;
    reviewTasks: number;
    claudeReports: number;
    githubActionsMinutes: number;
    ceilingMultiplier: number;
  };
  plan: Array<{
    intakeId: string;
    clientId: string | null;
    name: string;
    inputHash: string;
    issues: string[];
    missingMaps: string[];
    missingAiPairs: Array<{ keyword: string; platform: string }>;
    needsReport: boolean;
  }>;
  planHash: string;
}

export async function getCompletenessAudit(): Promise<CompletenessAuditDto> {
  return apiFetch<CompletenessAuditDto>('/api/cron/completeness-audit');
}

export interface RepairJobsResult {
  ok: boolean;
  planHash: string;
  ceiling: Record<string, number>;
  jobs: Array<{ intakeId: string; jobId: string; created: boolean }>;
}

export async function runRepairJobs(planHash: string): Promise<RepairJobsResult> {
  return apiFetch<RepairJobsResult>('/api/cron/repair-jobs', {
    method: 'POST',
    body: JSON.stringify({ planHash, confirm: planHash }),
  });
}

export interface MaintenanceRun {
  id: string;
  ranAt: number;
  cron: string | null;
  planHash: string | null;
  clientsTotal: number;
  clientsIncomplete: number;
  jobsCreated: number;
  jobsDeferred: number;
  estimate: Record<string, number> | null;
  ceiling: Record<string, number> | null;
  summary: unknown;
}

export async function getMaintenanceRuns(limit = 20): Promise<MaintenanceRun[]> {
  const { runs } = await apiFetch<{ runs: MaintenanceRun[] }>(`/api/cron/maintenance-runs?limit=${limit}`);
  return runs;
}

export interface ResolvePlaceResponse {
  ok: true;
  place: {
    placeId: string | null;
    source: 'entered' | 'resolved_link' | 'resolved_search';
    displayName: string | null;
    formattedAddress: string | null;
    rating: number | null;
    userRatingCount: number | null;
    primaryCategory: string | null;
  };
  row: IntakeRow;
}

export async function resolveIntakePlace(id: string): Promise<ResolvePlaceResponse> {
  return apiFetch<ResolvePlaceResponse>(
    `/api/intake/${encodeURIComponent(id)}/resolve-place`,
    { method: 'POST' },
  );
}

export interface IntakeLfKeywords {
  imported: boolean;
  mapsKeywords: string[];
  aiKeywords: string[];
}

// Read-only: what Local Falcon is actually tracking for this record's live
// client. The form merges these in so the planning record self-documents.
export async function getIntakeLfKeywords(id: string): Promise<IntakeLfKeywords> {
  return apiFetch<IntakeLfKeywords>(`/api/intake/${encodeURIComponent(id)}/lf-keywords`);
}

export interface LoginResult { email: string; status: 'created' | 'linked' | 'exists' | 'invalid' }
export interface ImportIntakeResponse {
  ok: true;
  clientId: string;
  logins: LoginResult[];
  magicLink: string | null;
  magicLinkExpiresAt: number | null;
  noLoginYet?: boolean;
  steps: { step: string; status: 'ok' | 'skipped' | 'failed'; message?: string }[];
}

export async function importIntake(id: string): Promise<ImportIntakeResponse> {
  return apiFetch<ImportIntakeResponse>(
    `/api/intake/${encodeURIComponent(id)}/import`,
    { method: 'POST' },
  );
}

export interface CampaignBreakdown { scans: number; credits: number }
export interface CreateCampaignResponse {
  ok: true;
  campaignKey: string;
  status: 'created' | 'started';
  lfEditUrl: string;
  keywords?: string[];
  breakdown: CampaignBreakdown;
  alreadyExists?: boolean;
}

// Create the standard Local Falcon scheduled campaign (paused) for this row.
export async function createIntakeCampaign(id: string): Promise<CreateCampaignResponse> {
  return apiFetch<CreateCampaignResponse>(
    `/api/intake/${encodeURIComponent(id)}/campaign/create`,
    { method: 'POST' },
  );
}

// Resume + run the (paused) campaign after the admin reviewed it on Local Falcon.
export async function startIntakeCampaign(id: string): Promise<{ ok: true; campaignKey: string; status: 'started' }> {
  return apiFetch(
    `/api/intake/${encodeURIComponent(id)}/campaign/start`,
    { method: 'POST' },
  );
}

// Push the row's Client Intake keywords to its linked LF campaign (Intake = source of truth).
export async function syncIntakeCampaignKeywords(id: string): Promise<{ ok: true; added: string[]; removed: string[] }> {
  return apiFetch(
    `/api/intake/${encodeURIComponent(id)}/campaign/sync-keywords`,
    { method: 'POST' },
  );
}

export interface CreateAllCampaignsResponse {
  ok: true;
  created: { id: string; name: string; campaignKey: string }[];
  linked: { id: string; name: string; campaignKey: string }[];   // pre-existing LF campaign linked (no duplicate)
  failed: { id: string; name: string; reason: string }[];
  processed: number;
  remaining: number;
  totalEligible: number;
}

// Batch-create paused LF campaigns for every ready intake row without one.
// Chunked server-side; call repeatedly until `remaining` is 0.
export async function createAllCampaigns(limit?: number): Promise<CreateAllCampaignsResponse> {
  return apiFetch<CreateAllCampaignsResponse>(
    '/api/intake/campaign/create-all',
    { method: 'POST', body: JSON.stringify(limit ? { limit } : {}) },
  );
}

export interface ReconcileRow {
  id: string;
  name: string | null;
  location: string | null;
  status: string;
  imported: boolean;
  campaignKey: string | null;
  campaignExists: boolean;
  brokenLink: boolean;
  mapsKeywords: number;
  lastScan: number | null;
  aiCount: number;
  verdict: 'ok' | 'not_synced' | 'broken_link' | 'no_campaign' | 'not_imported' | 'no_place';
}

// Live reconciliation: Intake × GBI clients × Local Falcon (broken links, missing, unsynced).
export async function reconcileIntake(): Promise<{ ok: true; lfError: string | null; rows: ReconcileRow[] }> {
  return apiFetch('/api/intake/reconcile');
}

export async function deleteIntake(id: string): Promise<void> {
  const res = await fetch(`/api/intake/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) {
    throw new ApiError(res.status, await res.text());
  }
}

// ── Client Status dashboard (admin) ──────────────────────────

export async function getAdminStatus(): Promise<ClientStatusResponse> {
  return apiFetch<ClientStatusResponse>('/api/admin/status');
}

export interface RerunResult {
  ok: true;
  target: 'aiInsight' | 'data';
  reportJobId?: string;
  alreadyQueued?: boolean;
  steps?: RunAllStep[];
}

// Bypasses apiFetch: on failure the row needs the server's parsed
// `{error, message}` body (not apiFetch's raw-text ApiError) so the UI can
// show a clean sentence instead of a stringified response.
export async function rerunClient(clientId: string, target: 'aiInsight' | 'data'): Promise<RerunResult> {
  const res = await fetch('/api/admin/rerun', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ clientId, target }),
  });
  const body = await res.json().catch(() => ({})) as Partial<RerunResult> & { error?: string; message?: string };
  if (!res.ok) throw new Error(body.message || body.error || `${res.status} ${res.statusText}`);
  return body as RerunResult;
}

// ── GBP action worklist (cross-client) ──────────────────────────────────────
// The whole book in one payload: every client's stored GBP Optimization
// analysis plus its action plan, so a sweep is one page instead of a hundred.
// Review fields are excluded server-side — reviews are their own report tab and
// are not part of the profile-editing worklist.

export type GbpActionOwner = 'auto' | 'approve';
export type GbpInsightTone = 'good' | 'warn' | 'bad';
export type GbpActionStatus = 'done' | 'skipped';

export interface GbpActionProgress {
  status: GbpActionStatus;
  markedAt: number;
  markedBy: string | null;
  /** A later report run rewrote this advice after it was marked — live again. */
  changedSinceMarked: boolean;
}

export interface GbpActionDto {
  /** Server-derived identity, stable across report regenerations. */
  actionKey: string;
  /** Server-derived digest of the current advice; stored when marking. */
  actionHash: string;
  progress: GbpActionProgress | null;
  title: string;
  owner: GbpActionOwner;
  category: string;
  field: string;
  where: string;
  current: string;
  target: string;
  pasteLabel: string | null;
  pasteBody: string | null;
  why: string;
  impact: number | null;
  effort: number | null;
  risk: number | null;
}

export interface GbpSuggestionDto {
  title: string;
  category: string;
  why: string;
  impact: number | null;
}

export interface GbpAnalysisDto {
  executiveSummary: string | null;
  profileInsight: { tone: GbpInsightTone; text: string } | null;
  mapsInsight: { tone: GbpInsightTone; text: string } | null;
  aiInsight: string | null;
  gaps: string[];
  diagnosis: { problem: string; opportunity: string } | null;
  competitorInsights: string[];
  vulnerableCompetitors: { name: string; weakness: string; howToExploit: string }[];
  websiteInsights: string[];
  optionalNotes: string[];
}

export interface GbpActionsClientDto {
  clientId: string;
  name: string;
  location: string;
  locationLabel: string | null;
  groupLabel: string | null;
  category: string | null;
  score: number | null;
  summary: string | null;
  generatedAt: number | null;
  hasReport: boolean;
  analysis: GbpAnalysisDto | null;
  actions: GbpActionDto[];
  suggestions: GbpSuggestionDto[];
}

export async function listGbpActions(): Promise<GbpActionsClientDto[]> {
  const { clients } = await apiFetch<{ clients: GbpActionsClientDto[] }>('/api/gbp-actions');
  return clients;
}

/** Mark one action done/skipped, or pass status null to clear the mark. */
export async function markGbpAction(
  clientId: string,
  action: Pick<GbpActionDto, 'actionKey' | 'actionHash'>,
  status: GbpActionStatus | null,
): Promise<GbpActionProgress | null> {
  const { progress } = await apiFetch<{ progress: GbpActionProgress | null }>(
    '/api/gbp-actions/progress',
    {
      method: 'POST',
      body: JSON.stringify({
        clientId,
        actionKey: action.actionKey,
        actionHash: action.actionHash,
        status,
      }),
    },
  );
  return progress;
}
